/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import { Resizable } from 're-resizable';

import { DrawerPosition } from './index';
import { useChildrenWithProps } from 'hooks';
import { fadeIn } from 'ui/theme/keyframes';

const SlideAnimationDuration = 0.3; // seconds

const Overlay = styled.div`
  display: ${({ show }) => (show ? 'block' : 'none')};
  z-index: ${({ zIndex = '999' }) => zIndex};
  animation: ${fadeIn} 100ms ease-out;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgba(55, 81, 126, 0.3);
`;

const Container = styled(
  // eslint-disable-next-line no-unused-vars
  React.forwardRef(({ position, ...rest }, ref) => (
    <Resizable ref={(component) => (ref.current = component?.resizable)} {...rest} />
  ))
)`
  position: fixed;
  border-radius: 0;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 13px rgba(0, 0, 0, 0.16);
  width: ${({ width = '50%' }) => width};
  max-width: ${({ maxWidth = '100%' }) => maxWidth};
  height: 100%;
  padding: 0;
  top: 0;
  bottom: 0;

  ${({ position }) => {
    switch (position) {
      case DrawerPosition.LEFT:
        return `
          left: 0;
          transform: translateX(-100%);
        `;
      case DrawerPosition.RIGHT:
      default:
        return `
          right: 0;
          transform: translateX(100%);
        `;
    }
  }}

  &.is-open {
    transform: none;
  }
  transition: transform ${SlideAnimationDuration}s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CloseIcon = styled.span.attrs(() => ({
  children: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23.189"
      height="23.188"
      viewBox="0 0 23.189 23.188"
    >
      <path
        id="close"
        d="M13.719,11.73,22.748,2.7A1.5,1.5,0,0,0,20.624.576l-9.03,9.029L2.565.576A1.5,1.5,0,0,0,.441,2.7L9.47,11.73.441,20.76a1.5,1.5,0,1,0,2.124,2.124l9.029-9.03,9.03,9.03a1.5,1.5,0,0,0,2.124-2.124Zm0,0"
        transform="translate(0 -0.136)"
        fill="#fff"
      />
    </svg>
  ),
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 35px;
  height: 35px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 2em;
  font-weight: 300;
  opacity: 1;
  cursor: pointer;
  transition: opacity 300ms ease-in-out;
  &:hover {
    opacity: 0.9;
  }
  > svg {
    width: 17px;
  }
`;

const Content = styled.div`
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-align: left;
  color: ${({ theme }) => theme.colors.grey};
  padding: ${({ padding = '1.3rem 1.5rem' }) => padding};
  width: 100%;
  height: 100%;
  overflow: auto;
`;

export const Drawer = ({
  show,
  children,
  position = DrawerPosition.RIGHT,
  closeOnOutsideClick = true,
  width,
  maxWidth,
  zIndex,
  onClose,
  stopClickPropagation = false,
  ...rest
}) => {
  const ref = React.useRef();
  const [isOpen, setIsOpen] = React.useState(false);
  const closeAllowed = React.useRef(false);
  const childrenWithProps = useChildrenWithProps(children, () => ({
    DrawerCloseButton: () => <CloseIcon onClick={handleClose} />,
  }));

  React.useEffect(() => {
    if (show) {
      // delay animation execution (needed to play animation correctly, even if the timeout is 0)
      setTimeout(() => setIsOpen(show), 0);
      // prevent fast close
      closeAllowed.current = false;
      const handle = setTimeout(() => (closeAllowed.current = true), 500);
      return () => clearTimeout(handle);
    }
  }, [show]);

  const handleClose = () => {
    setIsOpen(false);

    if (onClose) {
      // wait for animation to end
      setTimeout(() => onClose(), SlideAnimationDuration * 1000);
    }
  };

  const handleOutsideClick = (event) => {
    if (stopClickPropagation) {
      event.stopPropagation();
    }

    if (
      !closeOnOutsideClick ||
      !closeAllowed.current ||
      !ref.current ||
      ref.current.contains(event.target)
    ) {
      return;
    }

    handleClose();
  };

  return (
    <Overlay show={show} zIndex={zIndex} onMouseDown={handleOutsideClick}>
      <Container
        ref={ref}
        className={isOpen && 'is-open'}
        position={position}
        minWidth={width}
        maxWidth={maxWidth}
        defaultSize={{ width: width, height: '100%' }}
        style={{ position: 'fixed' }}
        enable={{ left: true }}
      >
        <Content {...rest}>{childrenWithProps}</Content>
      </Container>
    </Overlay>
  );
};
